import React, { useCallback, useRef } from "react";
import { Combobox, type ComboboxItem } from "@bloomberg/media.atomics";
import styles from "./FilterCombobox.module.css";
import {
  useIsLoading,
  useSelectedSecurityTypes,
  useWatchlistActions,
} from "~context/WatchlistContext";

const ALL_VALUE = "All";

// All possible asset classes — shown regardless of watchlist contents
const SECURITY_TYPES = [
  "Bond",
  "Commodity",
  "Currency",
  "Fund",
  "Index",
  "Stock",
] as const;

const ITEMS: ComboboxItem[] = [
  { value: ALL_VALUE, label: ALL_VALUE },
  ...SECURITY_TYPES.map((type) => ({ value: type, label: type })),
];

export default function SecurityTypeFilter() {
  const containerRef = useRef<HTMLDivElement>(null);
  const isLoading = useIsLoading();
  const selectedSecurityTypes = useSelectedSecurityTypes();
  const { setSelectedSecurityTypes } = useWatchlistActions();

  // "All" and specific types are mutually exclusive:
  // - Selecting "All" clears specific selections
  // - Selecting a specific type while "All" is active removes "All"
  const handleValueChange = useCallback(
    (values: string[]) => {
      if (values.length === 0) {
        setSelectedSecurityTypes([]);

        return;
      }

      const hadAll = selectedSecurityTypes.includes(ALL_VALUE);
      const hasAll = values.includes(ALL_VALUE);

      if (hasAll && !hadAll) {
        // User just selected "All" — collapse to only "All"
        setSelectedSecurityTypes([ALL_VALUE]);
      } else if (hasAll && values.length > 1) {
        // User selected a specific type while "All" was active — drop "All"
        setSelectedSecurityTypes(values.filter((v) => v !== ALL_VALUE));
      } else {
        setSelectedSecurityTypes(values);
      }
    },
    [setSelectedSecurityTypes, selectedSecurityTypes]
  );

  return (
    <div className={styles.filterContainer} ref={containerRef}>
      {/* Disabled during loading to prevent unmount/remount flicker on watchlist switch */}
      <Combobox
        labelText="Security Type"
        items={ITEMS}
        value={selectedSecurityTypes}
        onValueChange={handleValueChange}
        disabled={isLoading}
        multiple
        portalTarget={containerRef}
      />
    </div>
  );
}
